import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, CopyIcon, EyeIcon, Loader2Icon, MailIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuotePreview } from '../../components/admin/QuotePreview';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate, telHref } from '../../utils/format';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
export function QuoteDetail() {
  const {
    quoteId
  } = useParams();
  const navigate = useNavigate();
  const {
    quotes,
    cases,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    deleteQuote,
    createCaseFromQuote
  } = useAdminData();
  const quote = quotes.find(entry => entry.id === quoteId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setTitle(quote.title);
    setDescription(quote.description);
    setLines(quote.lines);
    setQuoteDate(quote.quoteDate);
    setValidUntil(quote.validUntil);
    setInternalNotes(quote.internalNotes);
    setCustomerNotes(quote.customerNotes);
  }, [quote?.id]);
  if (!quote) return <Navigate to="/admin/tilbud" replace data-id="element-816" data-mp="2smppox8fzvlu" />;
  const status = effectiveStatus(quote);
  const linkedCase = cases.find(item => item.id === quote.caseId || item.quoteId === quote.id);
  const locked = quote.status === 'Accepteret';
  async function handleSave() {
    if (saving) return;
    if (locked) {
      const confirmed = window.confirm('Tilbuddet er accepteret af kunden. Er du sikker på, at du vil ændre pris og indhold?');
      if (!confirmed) return;
    }
    setSaving(true);
    setError('');
    const result = await updateQuote(quote.id, {
      title,
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-817" data-mp="3gijl6tgplqfe">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-818" data-mp="3cgpmgqq2qwhv">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-819" data-mp="d35h93yu5if9" />
        Tilbud
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-820" data-mp="84ax9yp4tntk">
        <div data-id="element-821" data-mp="39yelqvhcgiv">
          <p className="text-sm text-ink-muted" data-id="element-822" data-mp="39evi6u3fm39">{quote.quoteNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-823" data-mp="3veisi1vzvmjh">
            {quote.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-824" data-mp="r0kakjs7maci">
            {quote.customerName} · {formatKr(quote.total)} inkl. moms
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-825" data-mp="bhrg2c5to566">
          <QuoteStatusBadge status={status} data-id="element-826" data-mp="giyma6sbgwaq" />
          <SelectField id="tilbud-status" label="Status" className="w-52" value={quote.status} onChange={async event => {
          setWorking('status');
          await setQuoteStatus(quote.id, event.target.value as QuoteStatus);
          setWorking('');
        }} options={quoteStatuses.filter(option => option !== 'Udløbet').map(option => ({
          value: option,
          label: option
        }))} data-id="element-827" data-mp="gfsz4x3904hn" />
        </div>
      </div>

      {status === 'Udløbet' && <p className="mt-5 flex items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-828" data-mp="ph0v38iakt8k">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-829" data-mp="1d6lgdy1x42l0" />
          Tilbuddet er udløbet ({formatDate(quote.validUntil)}). Forlæng gyldigheden, eller lav en kopi.
        </p>}

      {quote.status === 'Accepteret' && <section className="mt-5 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-830" data-mp="2xpxisiel1lfi">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-831" data-mp="3ce1u31bb2uo9">
            <div data-id="element-832" data-mp="37g09gby8enao">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-833" data-mp="37hl5y743wgb6">
                {linkedCase ? 'Sag allerede oprettet' : 'Tilbuddet er accepteret'}
              </h2>
              <p className="mt-1 text-[15px] text-ink-soft" data-id="element-834" data-mp="2eso0j4kzudzu">
                {linkedCase ? `Sag #${linkedCase.number} — ${linkedCase.title}` : 'Opret sagen, så arbejdet kan planlægges og udføres.'}
              </p>
            </div>
            {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className={buttonClasses('secondary', 'md')} data-id="element-835" data-mp="8xlay15h28o1">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-836" data-mp="3yi6ahtem4gl" />
                Åbn sag
              </Link> : <Button size="md" disabled={working === 'case'} onClick={async () => {
          setWorking('case');
          setError('');
          const created = await createCaseFromQuote(quote);
          setWorking('');
          if (!created) {
            setError('Sagen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/sager/${created.id}`);
        }} data-id="element-837" data-mp="b4ud0jldbijm">
                {working === 'case' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-838" data-mp="28ld73thahwy" />
                    Opretter …
                  </> : 'Opret sag fra tilbud'}
              </Button>}
          </div>
        </section>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-839" data-mp="3ao50znbdr84m">
        <div className="space-y-5" data-id="element-840" data-mp="1rttwnda32y3u">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-841" data-mp="1rssgwvi4hnfs">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-842" data-mp="1nub8m8myu6ws">Opgave</h2>
            {locked && <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-843" data-mp="1iw9tfysnurql">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-844" data-mp="18w0sy8rmyk5e" />
                Tilbuddet er accepteret. Ændringer i pris og indhold skal bekræftes, før de gemmes.
              </p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-845" data-mp="1035xg6rz02st">
              <TextField id="td-titel" label="Tilbudstitel" className="sm:col-span-2" value={title} onChange={event => {
              setTitle(event.target.value);
              setSaved(false);
            }} data-id="element-846" data-mp="v4ukvoaaje6w" />
              <TextField id="td-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
              setQuoteDate(event.target.value);
              setSaved(false);
            }} data-id="element-847" data-mp="2a0cdnkounv48" />
              <TextField id="td-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => {
              setValidUntil(event.target.value);
              setSaved(false);
            }} data-id="element-848" data-mp="250zsr5hjgy4a" />
            </div>
            <TextArea id="td-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => {
            setDescription(event.target.value);
            setSaved(false);
          }} data-id="element-849" data-mp="1hdromqvujv3u" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-850" data-mp="sqsbj3bne8hr">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-851" data-mp="opqb4j5zvqe7">Tilbudslinjer</h2>
            <div className="mt-5" data-id="element-852" data-mp="12phuz0j70104">
              <QuoteLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-853" data-mp="12qj4v1oh2hg9" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-854" data-mp="1bo37of1zglsz">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-855" data-mp="17o2vl12cwmp3">Noter</h2>
            <TextArea id="td-kundenoter" label="Bemærkninger til kunden" className="mt-5" rows={3} value={customerNotes} onChange={event => {
            setCustomerNotes(event.target.value);
            setSaved(false);
          }} data-id="element-856" data-mp="1leo7g5te7wdh" />
            <TextArea id="td-internenoter" label="Interne noter" className="mt-5" rows={3} value={internalNotes} onChange={event => {
            setInternalNotes(event.target.value);
            setSaved(false);
          }} data-id="element-857" data-mp="63b7ln7heh2b" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-858" data-mp="3of7r1a4hnv7d">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-859" data-mp="p1e38kujbc93" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-860" data-mp="g18vuapccdux">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-861" data-mp="fzo2goerj0yn">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-862" data-mp="b4qrvd1u4jjn" />
                  Gemmer …
                </> : 'Gem ændringer'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPreviewing(true)} data-id="element-863" data-mp="1bauno8qpxrot">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-864" data-mp="1g9p2z0ph8y46" />
              Forhåndsvis
            </Button>
            {quote.status === 'Kladde' && <Button variant="dark" size="lg" disabled={working === 'sent'} onClick={async () => {
            setWorking('sent');
            await setQuoteStatus(quote.id, 'Sendt');
            setWorking('');
          }} data-id="element-865" data-mp="1k9psbge46tq0">
                Marker som sendt
              </Button>}
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-866" data-mp="23x5kr9f49agv">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-867" data-mp="28027ncbovsqm">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-868" data-mp="dmub9g82f92v" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>
        </div>

        <aside className="space-y-5" data-id="element-869" data-mp="1gzktywwbfz7o">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-870" data-mp="1bzgd35rhx2gx">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-871" data-mp="1py69skael04r">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-872" data-mp="1lx2uq006j84l">
              {quote.customerName || '—'}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-873" data-mp="1zvavkj0c6tl1">
              <li data-id="element-874" data-mp="1uyas8fuvg4xm">{quote.customerPhone || '—'}</li>
              <li className="break-all" data-id="element-875" data-mp="1uvesclzgr66j">{quote.customerEmail || '—'}</li>
              <li data-id="element-876" data-mp="29l40j15z4agb">{quote.customerAddress || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-877" data-mp="24msqoqfohzy3">
              {quote.customerPhone && <LinkButton href={telHref(quote.customerPhone)} variant="secondary" size="sm" data-id="element-878" data-mp="2io53iszjllfr">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-879" data-mp="2dos7bhms720i" />
                  Ring
                </LinkButton>}
              {quote.customerEmail && <LinkButton href={`mailto:${quote.customerEmail}`} variant="secondary" size="sm" data-id="element-880" data-mp="328n4xbo2e6gm">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-881" data-mp="2o9dzn71ecwrf" />
                  Send e-mail
                </LinkButton>}
            </div>
            {quote.customerId && <Link to={`/admin/kunder/${quote.customerId}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-882" data-mp="2ejkr6ewjwxre">
                Se kundeprofil
              </Link>}
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-883" data-mp="29jol8a4amw97">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-884" data-mp="1vkgjb9gqxa1b">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-885" data-mp="20ii2due699cs">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-886" data-mp="1sky7p16e9ld5">
                Åbn sag
              </Link>
            </section>}

          <section className="rounded-card border border-line bg-white p-6" data-id="element-887" data-mp="1eryuj5u3438r">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-888" data-mp="19scgwvacgkzf">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-889" data-mp="14t9ap7imo8pv">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-890" data-mp="14ry8vd8qtb1e">
                <dt className="text-ink-soft" data-id="element-891" data-mp="zv7t6r0uohgi">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-892" data-mp="vskymicsoifm">{formatKr(quote.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-893" data-mp="2u7xn38r7xqnw">
                <dt className="text-ink-soft" data-id="element-894" data-mp="2u7edcfbq28ky">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-895" data-mp="2q6cgg55a28m5">{formatKr(quote.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-896" data-mp="bzmir9yv0y98">
                <dt className="font-medium text-ink" data-id="element-897" data-mp="gxxuxrcmnkum">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-898" data-mp="gw3a0d1cvsqj">{formatKr(quote.total)}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-ink-muted" data-id="element-899" data-mp="pq84r7jm1obc">
              Mailudsendelse til kunden tilføjes senere via en sikker backend.
            </p>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-900" data-mp="3p77mbpd51xbo">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-901" data-mp="3u595096njumm">Handlinger</h2>
            <div className="mt-4 flex flex-col gap-2" data-id="element-902" data-mp="1z3ve5836zmk">
              <Button variant="secondary" size="sm" disabled={working === 'copy'} onClick={async () => {
              setWorking('copy');
              const copy = await duplicateQuote(quote);
              setWorking('');
              if (copy) navigate(`/admin/tilbud/${copy.id}`);
            }} data-id="element-903" data-mp="709nq1xu4kvk">
                <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-904" data-mp="3agzoperyylih" />
                Dupliker tilbud
              </Button>
              <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-905" data-mp="3of6dm5exxb60">
                <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-906" data-mp="2qqmku0macc85" />
                Slet tilbud
              </button>
            </div>
          </section>
        </aside>
      </div>

      {previewing && <QuotePreview quoteNumber={quote.quoteNumber} title={title} description={description} lines={lines} quoteDate={quoteDate} validUntil={validUntil} customerNotes={customerNotes} customer={{
      name: quote.customerName,
      address: quote.customerAddress,
      email: quote.customerEmail,
      phone: quote.customerPhone
    }} onClose={() => setPreviewing(false)} data-id="element-907" data-mp="2d12n597ka55p" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-tilbud-titel" data-id="element-908" data-mp="ilnf390u65ji">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-909" data-mp="1b8zqqo5gksy1">
            <h2 id="slet-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-910" data-mp="3i08y380h6k7j">
              Er du sikker på, at du vil slette dette tilbud?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-911" data-mp="3mwpo67r8j8n9">
              {quote.quoteNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-912" data-mp="dhd5jhrprbui">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-913" data-mp="8jbi6kn98qd8" />
                Tilbuddet er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men mister
                henvisningen til tilbuddet.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-914" data-mp="w9fmyvc6iw5t">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-915" data-mp="rfkylelmpt0e">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteQuote(quote.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Tilbuddet kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/tilbud');
          }} data-id="element-916" data-mp="mikq6x1b4e64">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-917" data-mp="25hsbr30aff0m" />
                    Sletter …
                  </> : 'Slet tilbud'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-918" data-mp="2f8d9ypc1uhj0">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}